<template>
  <div
    class="map-wrapper"
    :class="{
      'map-wrapper-nosidebar': isNoSideBar
    }">
    <h3>Monitor Bencana</h3>
    <div class="columns controls">
      <div class="column columns is-half">
        <div class="column is-one-quarter item" @click="toggleItemDisabled('earthquake')"
           :class="itemClass('earthquake')">
          <img :src="markerIcons.earthquake.iconRetinaUrl"/><br/>
          Gempa Bumi
        </div>
        <div class="column is-one-quarter item" @click="toggleItemDisabled('fire')"
           :class="itemClass('fire')">
          <img :src="markerIcons.fire.iconRetinaUrl"/><br/>
          Kebakaran
        </div>
        <div class="column is-one-quarter item" @click="toggleItemDisabled('flood')"
           :class="itemClass('flood')">
          <img :src="markerIcons.flood.iconRetinaUrl"/><br/>
          Banjir
        </div>
        <div class="column is-one-quarter item" @click="toggleItemDisabled('landslide')"
             :class="itemClass('landslide')">
          <img :src="markerIcons.landslide.iconRetinaUrl"/><br/>
          Longsor
        </div>
      </div>
      <div class="column columns is-half">
        <div class="column is-one-quarter item" @click="toggleItemDisabled('tsunami')"
        :class="itemClass('tsunami')">
          <img :src="markerIcons.tsunami.iconRetinaUrl"/><br/>
          Tsunami
        </div>
        <div class="column is-one-quarter item" @click="toggleItemDisabled('shelter')"
           :class="itemClass('shelter')">
          <img :src="markerIcons.house.iconRetinaUrl"/><br/>
          Shelter
        </div>
        <div class="column is-one-quarter item" @click="toggleItemDisabled('bmkgearthquake')"
           :class="itemClass('bmkgearthquake')">
          <img :src="markerIcons.bmkgearthquake.iconRetinaUrl"/><br/>
          Gempa BMKG
        </div>
      </div>
    </div>

    <h6>Last Update: {{new Date(lastUpdate).toUTCString()}} (setiap {{updateInterval}} detik)</h6>
    <LMap
      :zoom="zoom"
      :center="center"
      :options="mapOptions"
      :fullscreenControl="true"
      @update:center="centerUpdate"
      @update:zoom="zoomUpdate"
      >
      <LTileLayer :url="url" :attribution="attribution" />
      <section v-if="!isDisabled('shelter')">
        <LMarker
          v-for="s in shelters"
          :key="s._id"
          :lat-lng="getLatLng(s.location)"
          :icon="getShelterMarker(s.code)">
          <LPopup>
            <div>
              Name: {{s.name}}<br/>
              Status: {{s.status}} ({{getShelterUsage(s.code)}}%)<br/>
              Occupancy: {{getStatus(s.code).occupancyStatus}}<br/>
              Capacity: {{getStatus(s.code).maxOccupancy}}<br/>
              Num of Refugees: {{getTotalRefugees(s.code)}}<br/>
              Address: {{s.address || '-'}}<br/>
              <router-link :to="{name: 'ShelterStatus:Create', params: {id: s.code}}">
                Report Status
              </router-link>
            </div>
          </LPopup>
        </LMarker>
      </section>
      <section
        v-for="d in damages"
        :key="d._id">
        <LMarker
          v-if="!isDisabled(d.disasterType)"
          :lat-lng="getLatLng(d.location)"
          :icon="getDamageMarker(d)">
          <LPopup>
            <div>
              Disaster: {{getDisasterName(d.disasterCode)}}<br/>
              Type: {{d.disasterType}}<br/>
              Level: {{d.level}}<br/>
              Waktu: {{d.time}}<br/>
              Lokasi: {{d.locationName}}<br/>
              Posisi: {{d.location}}<br/>
              <div v-if="d.disasterType === 'EARTHQUAKE'">
                Kedalaman: {{getEarthQuakeData(d).depth}}<br/>
                Magnitude: {{getEarthQuakeData(d).magnitude}}<br/>
                Keterangan: {{getEarthQuakeData(d).description}}<br/>
              </div>
            </div>
          </LPopup>
        </LMarker>
      </section>
      <section v-if="!isDisabled('bmkgearthquake')">
        <LMarker
            v-for="(s, idx) in sensedQuakes"
            :key="'sensed-' + idx"
            :lat-lng="getLatLng(s.point.coordinates)"
            :icon="markers.bmkgearthquake">
          <LPopup>
            <div>
              Tanggal: {{s.Tanggal}}<br/>
              Lokasi: {{s.Dirasakan}}<br/>
              Kedalaman: {{s.Kedalaman}}<br/>
              Posisi: {{s.Posisi}}<br/>
              Magnitude: {{s.Magnitude}}<br/>
              Keterangan: {{s.Keterangan}}<br/>
            </div>
          </LPopup>
        </LMarker>
      </section>
    </LMap>
  </div>
</template>

<style lang="scss" scoped>
.map {
  &-wrapper {
    position: absolute;
    top: 50px;
    bottom: 20px;
    right: 0px;
    padding: 20px;
    height: 85vh;

    @media only screen and (min-width: 768px) {
      left: 300px;
    }

    &-nosidebar {
      left: 0px;
    }
  }
}
.controls {
  text-align: center;

  .disabled {
    opacity: 0.4;
    background-color: #ddd;
  }

  .item {
    font-weight: bold;
    cursor: pointer;

    &:hover {
      opacity: 0.4;
    }

    &-disabled {
      opacity: 0.2;
      background-color: #bbb;
    }
  }
}
</style>

<script>
import {mapGetters, mapActions} from 'vuex'
import {latLng, Icon} from 'leaflet'
import {LMap, LTileLayer, LMarker, LPopup} from 'vue2-leaflet'

const DEFAULT_CENTER = latLng(-0.599094, 117.504444)

const customIcon = Icon.extend({
  options: {
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
  }
})

const MARKER_ICONS = {
  earthquake: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/earthquake/marker-earthquake-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/earthquake/marker-earthquake-1x.png')
  },
  fire: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/fire/marker-fire-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/fire/marker-fire-1x.png')
  },
  flood: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/flood/marker-flood-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/flood/marker-flood-1x.png')
  },
  landslide: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/landslide/marker-landslide-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/landslide/marker-landslide-1x.png')
  },
  tsunami: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/tsunami/marker-tsunami-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/tsunami/marker-tsunami-1x.png')
  },
  bmkgearthquake: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/bmkg-earthquake/marker-bmkg-earthquake-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/bmkg-earthquake/marker-bmkg-earthquake-1x.png')
  },
  // non disaster
  house: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/house/marker-house-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/house/marker-house-1x.png')
  },
  housered: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/house/marker-house-red-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/house/marker-house-red-1x.png')
  },
  housegreen: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/house/marker-house-green-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/house/marker-house-green-1x.png')
  },
  houseyellow: {
    iconRetinaUrl: require('@/assets/icons/markers/customs/house/marker-house-yellow-2x.png'),
    iconUrl: require('@/assets/icons/markers/customs/house/marker-house-yellow-1x.png')
  }
}

const MARKERS = {
  earthquake: new customIcon({...MARKER_ICONS.earthquake}),
  fire: new customIcon({...MARKER_ICONS.fire}),
  flood: new customIcon({...MARKER_ICONS.flood}),
  landslide: new customIcon({...MARKER_ICONS.landslide}),
  tsunami: new customIcon({...MARKER_ICONS.tsunami}),
  bmkgearthquake: new customIcon({...MARKER_ICONS.bmkgearthquake}),
  house: new customIcon({...MARKER_ICONS.house}),
  housered: new customIcon({...MARKER_ICONS.housered}),
  housegreen: new customIcon({...MARKER_ICONS.housegreen}),
  houseyellow: new customIcon({...MARKER_ICONS.houseyellow})
}

export default {
  name: 'ShelterStatus',
  data () {
    return {
      isNoSideBar: false,
      zoom: 5,
      center: DEFAULT_CENTER,
      url: "http://{s}.tile.osm.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      marker: DEFAULT_CENTER,
      mapOptions: {
        zoomSnap: 0.5
      },
      currentZoom: 0,
      currentCenter: 0,
      disabledItems: [],
      intervalId: null,
      lastUpdate: Date.now(),
      updateInterval: 5
    }
  },
  mounted () {
    this.init()
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup
  },
  computed: {
    ...mapGetters(['visibleSideBar']),
    ...mapGetters('Shelter', ['shelters']),
    ...mapGetters('Damage', ['damages']),
    ...mapGetters('Bmkg', ['sensedQuakes']),
    ...mapGetters('DisasterName', ['names']),
    ...mapGetters('ShelterStatus', {
      shelterStatuses: 'statuses'
    }),
    statusMapped () {
      return this.shelterStatuses.reduce((acc, i) => {
        return {
          ...acc,
          [i.shelterCode]: i
        }
      }, {})
    },
    disasterNamesMapped () {
      return this.names.reduce((acc, i) => {
        return {
          ...acc,
          [i.code]: i
        }
      }, {})
    },
    markers () {
      return MARKERS
    },
    markerIcons () {
      return MARKER_ICONS
    }
  },
  methods: {
    ...mapActions('Shelter', {
      fetchShelters: 'fetch'
    }),
    ...mapActions('Damage', {
      fetchDamages: 'fetch'
    }),
    ...mapActions('ShelterStatus', {
      fetchShelterStatuses: 'fetch'
    }),
    ...mapActions('DisasterName', {
      fetchDisasterNames: 'fetch'
    }),
    ...mapActions('Bmkg', ['getSensedQuakes']),
    zoomUpdate(zoom) {
      this.currentZoom = zoom
    },
    centerUpdate(center) {
      this.currentCenter = center
      this.marker = center
    },
    init () {
      this.currentZoom = this.zoom
      this.currentCenter = this.center

      this.startScheduler()
    },
    fetchData () {
      // fetch shelters
      this.fetchShelters()

      // fetch damages
      this.fetchDamages()

      // fetch quakes
      this.getSensedQuakes()

      // fetch disaster names
      this.fetchDisasterNames()
    },
    startScheduler () {
      clearInterval(this.intervalId)
      setInterval(() => {
        this.lastUpdate = Date.now()
        this.fetchData()
      }, this.updateInterval * 1000)
    },
    getLatLng (str) {
      return latLng(...str.split(','))
    },
    getDisasterName (code) {
      const disaster = this.disasterNamesMapped[code] || {}
      return disaster.name
    },
    getStatus (code) {
      return this.statusMapped[code] || {}
    },
    getShelterUsage (code) {
      const status = this.getStatus(code)
      if (!status._id) return 0

      const {maxOccupancy = 0, numOfRefugees = 0, numOfRefugeesVoluntary = 0} = status
      const totalRefugees = Number(numOfRefugees) + Number(numOfRefugeesVoluntary)
      if (totalRefugees < 1 || maxOccupancy < 1) return 0

      return Math.round(totalRefugees * 100 / Number(maxOccupancy))
    },
    getShelterMarker (code) {
      const usage = this.getShelterUsage(code)

      if (usage < 0) return MARKERS.house
      if (usage <= 50) return MARKERS.housegreen
      if (usage <= 80) return MARKERS.houseyellow
      return MARKERS.housered
    },
    getTotalRefugees (code) {
      const obj = this.getStatus(code)
      return Number(obj.numOfRefugees)
        + Number(obj.numOfRefugeesVoluntary)
    },
    getDamageMarker (d) {
      const type = d.disasterType || ''
      return MARKERS[type.toLowerCase()] || MARKERS.earthquake
    },
    itemClass (i) {
      return {
        'item-disabled': this.isDisabled(i)
      }
    },
    isDisabled (i = '') {
      return this.disabledItems.indexOf(i.toLowerCase()) > -1
    },
    toggleItemDisabled (i) {
      const item = i.toLowerCase()
      const disabled = this.isDisabled(i)
      if (disabled) {
        this.disabledItems = this.disabledItems.filter(i => i !== item)
        return
      }
      this.disabledItems = this.disabledItems.concat(item)
    },
    getEarthQuakeData (s) {
      return s.earthquake || {}
    }
  },
  watch: {
    visibleSideBar (v) {
      if (v) {
        this.isNoSideBar = false
        return
      }

      setTimeout(() => {
        this.isNoSideBar = true
      }, 600)
    },
    shelters () {
      // const filters = this.shelters.map(i => 'shelterCode[$in]=' + i.code)
      this.fetchShelterStatuses()
    }
  }
}
</script>
